import React from 'react';
import { Callout, Step, DocHeading, DocTable, Shot } from './components';

function PageFastMount({ navigate }) {
  return (
    <div>
      <p className="text-sm text-brand-400 font-medium mb-2">Tools</p>
      <h1 className="font-display text-3xl font-bold text-white tracking-tight mb-3">Fast Mount</h1>
      <p className="text-gray-400 leading-relaxed mb-8">
        Mounting normally means standing still while the animation plays. Fast Mount gets you on the
        mount without that dead time, can wear a faster cloak just for the mounting, and keeps you
        on the mount during a run. It is on{' '}
        <strong className="text-white">TOOLS &rsaquo; Fast mount</strong>: the{' '}
        <strong className="text-white">FAST MOUNT</strong> card on the left, the{' '}
        <strong className="text-white">CLOAK</strong> card on the right.
      </p>

      <DocHeading>Set It Up</DocHeading>
      <Step n={1} title="Choose How">
        The <strong>How</strong> dropdown offers <em>Jump first</em> and{' '}
        <em>Hold the movement back</em> &mdash; see the table below. Both work; they trade off
        differently.
      </Step>
      <Step n={2} title="With Jump first, pick the Jump skill">
        The <strong>Jump skill</strong> dropdown lists the skills on your action bars by their game
        id. The ones that actually move your character are marked <em>- moves you</em> &mdash; pick
        one of those. While it is on <em>not set</em>, the card says{' '}
        <em className="text-orange-300">Pick the jump skill first.</em> and nothing happens. The
        other method does not need a skill, so the dropdown is not shown.
      </Step>
      <Step n={3} title="Check the Mount line">
        There is nothing to choose here: the bot uses the game&rsquo;s own mount slot. The{' '}
        <strong>Mount</strong> line shows, in green, the name of the mount it found there. If it says{' '}
        <em className="text-orange-300">slot N is empty</em>, put your mount back into the mount
        slot of your action bar. <em>bar not readable</em> in grey just means the action bar could
        not be read at that moment (for example on a loading screen).
      </Step>
      <Step n={4} title="Optional: bind Mount now">
        A key that mounts immediately. It works while you play by hand too &mdash; the route does
        not have to be running, and the key fires even with the card switched off. It only reacts
        while the game window is the active window.
      </Step>
      <Step n={5} title="Switch the FAST MOUNT card on">
        The switch in the card&rsquo;s title row turns on automatic mounting: while the route runs,
        the bot gets back on the mount by itself whenever it makes sense. A green dot appears after{' '}
        <strong>TOOLS</strong> in the top bar while it is on.
      </Step>

      <Shot
        name="fast-mount-01-tab.png"
        tab="TOOLS › Fast mount"
        caption="The Fast mount tab: the FAST MOUNT card with its switch on the left, the CLOAK card on the right."
        shoot="TOOLS › Fast mount fül, a FAST MOUNT kártya kapcsolója BE, How = Jump first, kiválasztott Jump skill (a „- moves you” jelzéssel), futó route mellett. Látsszon a zöld Mount sor a hátas nevével, az Auto sor (pl. „already mounted”), a State sor, és jobb oldalt a CLOAK kártya kiválasztott köpennyel és a Swaps with sorral. Mindkét ADVANCED csukva."
      />

      <DocHeading>The Two Methods</DocHeading>
      <DocTable
        headers={['How', 'What it does', 'Trade-off']}
        rows={[
          [
            <strong className="text-white">Jump first</strong>,
            'Jumps forward, then mounts. During a run the jump goes along the route, lands away from monsters and does not fly over a pack. If there is no good spot ahead, it mounts without a jump.',
            'The route stops for a moment and continues as soon as you are on the mount. Needs a jump skill.',
          ],
          [
            <strong className="text-white">Hold the movement back</strong>,
            'Keeps running: the movement is held back from the server for a moment, so the mount is not interrupted.',
            'No stopping and no jump skill. The character may slide for a moment.',
          ],
        ]}
      />
      <Callout type="info">
        With <strong>Hold the movement back</strong> a line appears under the dropdown.{' '}
        <strong>Held back</strong> is a counter that grows as it works. Before the first mount it
        reads <strong>Hold-back</strong> <em>on first run</em> &mdash; that is normal. If it ever
        says <strong className="text-red-400">failed</strong>, the holding back does not work on
        your PC: mounting then goes the slow way, and while running it can be interrupted. Switch to
        Jump first.
      </Callout>
      <p className="text-sm text-gray-400 mb-4">
        With <strong className="text-white">Mount now</strong> and no route running, Jump first
        jumps a few metres in the direction you are running. If you are standing still there is no
        direction to jump in, so it simply mounts without a jump.
      </p>

      <DocHeading>Staying Mounted During a Run</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        With the card switched on, the bot keeps checking whether you are still on the mount while
        the route runs, and gets back on when you have been knocked off. The{' '}
        <strong className="text-white">Auto</strong> line tells you what it is waiting for. Green{' '}
        <em>ready</em> means nothing is in the way and a mount is starting right now, so it usually
        changes to <em>running</em> a moment later.
      </p>
      <DocTable
        headers={['Auto says', 'What it means']}
        rows={[
          [<strong className="text-white">ready</strong>, 'Nothing is in the way; a mount is starting.'],
          [<strong className="text-white">running</strong>, 'A mounting is in progress right now; the State line shows where it is.'],
          [<strong className="text-white">the route is not running</strong>, 'Automatic mounting only works during a run. Use Mount now otherwise.'],
          [<strong className="text-white">going through a gate</strong>, 'It waits until the map change is over.'],
          [<strong className="text-white">the route is holding: &hellip;</strong>, 'The run is paused by something else (for example a fight or picking up loot), named after the colon.'],
          [<strong className="text-white">already mounted</strong>, 'You are on the mount. Nothing to do.'],
          [<strong className="text-white">not enough rail left</strong>, 'Less than 25 m of the route is left ahead — not worth it.'],
          [<strong className="text-white">just tried - waiting a bit</strong>, 'A short pause of a few seconds between attempts.'],
          [<strong className="text-white">waiting for the cloak to swap back</strong>, 'The previous mounting has just finished; it gives your own cloak time to come back first.'],
          [<strong className="text-white">the jump is on cooldown</strong>, 'Jump first waits for the jump skill.'],
          [<strong className="text-white">no jump skill on the configured slot</strong>, 'The skill was moved off the slot you picked. Pick it again in the Jump skill dropdown.'],
          [<strong className="text-white">pick the jump skill first</strong>, 'Jump first is selected, but no jump skill is set.'],
          [<strong className="text-white">cannot mount on this map</strong>, 'Three mounts failed on this map without a success in between (some maps do not allow mounts). It stops trying here and tries again on the next map.'],
        ]}
      />
      <Callout type="info">
        <strong>Monsters next to you do not stop it.</strong> And when the combat system jumps forward
        on its own, the bot sends the mount inside that jump: no cloak swap, no stop, no extra jump
        (State: <em>mounting inside a combat jump</em>). These are counted on the{' '}
        <strong>Free (combat jump)</strong> line. If one does not take, the normal automatic mounting
        simply tries again a little later.
      </Callout>
      <Callout type="success">
        The mount command is an on/off switch in the game &mdash; sent while you are mounted, it
        would take you off. The bot therefore never sends it unless the game has just confirmed that
        you are on foot. That is why you may see <em>checking if mounted...</em> for a moment.
      </Callout>

      <DocHeading>Cloak (optional)</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        If you own a cloak that makes mounting faster, the bot can put it on for the mounting and
        swap your own back right after. Pick it in the <strong className="text-white">Cloak</strong>{' '}
        dropdown &mdash; it lists the cloaks in your bag. Leave it on <em>none</em> to mount without
        one. The <strong className="text-white">Swaps with</strong> line shows the cloak you are
        wearing now, the one that comes back afterwards.
      </p>
      <DocTable
        headers={['You see', 'What it means']}
        rows={[
          [<strong className="text-white">(bag not read yet)</strong>, 'The bot has not read your bag yet. Wait a moment.'],
          [<strong className="text-white">(no cloak in the bag)</strong>, 'There is no cloak to choose from in your bag.'],
          [<span className="text-orange-300">&hellip; is not in the bag right now.</span>, 'The chosen cloak is not in the bag (you are wearing it, or it is elsewhere). The setting is kept; mounting simply happens without it.'],
          [<strong className="text-white">Swaps with: checking...</strong>, 'It is still asking the game which cloak you wear.'],
        ]}
      />
      <Callout type="warning">
        After mounting, the bot checks that your own cloak really came back, and tries once more if
        not. If it still fails, a notification says{' '}
        <em>The fast-mount cloak could not be swapped back</em> and the State line reads{' '}
        <em>the cloak did not come back</em>. Your own cloak is then still in the bag &mdash; put it
        back on by hand.
      </Callout>

      <DocHeading>Timing (ADVANCED)</DocHeading>
      <p className="text-sm text-gray-400 mb-4">
        Each card has its own <strong className="text-white">ADVANCED</strong> section with one
        slider. Leave them alone unless something below tells you otherwise.
      </p>
      <DocTable
        headers={['Setting', 'Card', 'Default', 'What it does']}
        rows={[
          [
            <strong className="text-white">Delay before mounting</strong>,
            'FAST MOUNT',
            '0 ms',
            'How long to wait after the jump before getting on the mount. 0 is the fastest. If you often see "did not get on the mount" while running, try 150–250 ms.',
          ],
          [
            <strong className="text-white">Cloak back after</strong>,
            'CLOAK',
            '700 ms',
            'How long after mounting the fast cloak is swapped back for your own one.',
          ],
        ]}
      />

      <DocHeading>Status Lines</DocHeading>
      <DocTable
        headers={['Line', 'What it means']}
        rows={[
          [<strong className="text-white">State</strong>, 'What it is doing right now, or how the last attempt ended: done, did not get on the mount, mount refused - one more try, no cloak - plain mount, and so on.'],
          [<strong className="text-white">Last run</strong>, 'With Jump first: how long the last attempt took, in milliseconds — putting the cloak on, then (counted from the jump) until the mount was sent and until the game confirmed you were mounted.'],
          [<strong className="text-white">Hold</strong>, <>How long the route stood still for Jump first. Green <em>released by us</em> is normal. Orange <em>ran to the &hellip; ms timeout</em> means the mount was not confirmed and the route waited the full time. With Hold the movement back, or with Mount now and no route running, nothing stood still, so ignore this line.</>],
          [<strong className="text-white">Done / failed</strong>, 'Session counters.'],
          [<strong className="text-white">Free (combat jump)</strong>, 'How many times it sent the mount inside a jump the combat system made anyway — the cheapest case, no extra time at all.'],
          [<strong className="text-white">Last send</strong>, <>On the CLOAK card: whether the last jump and mount commands actually went out. <em>jump ok / mount ok</em> is normal, and <em>-</em> means that command has not been sent yet (with Hold the movement back the jump always stays <em>-</em>). If the line turns red, the word that is not <em>ok</em> names a command that was never sent &mdash; it was not the game refusing.</>],
        ]}
      />

      <DocHeading>Why Doesn&rsquo;t It Mount?</DocHeading>
      <DocTable
        headers={['Symptom', 'What to do']}
        rows={[
          [<strong className="text-white">did not get on the mount (while running)</strong>, 'The character was still in the jump when the mount was sent. Raise Delay before mounting to 150–250 ms, or switch How to Hold the movement back.'],
          [<strong className="text-white">cannot tell if we got on</strong>, 'The game did not answer in time. It is counted as a failure and tried again later.'],
          [<strong className="text-white">the jump did not go out - retrying</strong>, 'The jump skill did not fire (for example it was on cooldown). This does not count against the map; it simply tries again shortly.'],
          [<strong className="text-white">Mount: slot N is empty</strong>, 'Your mount is not in the mount slot of the action bar. Put it back.'],
          [<strong className="text-white">Mount now does nothing</strong>, 'You are already mounted (State: already mounted), the game window is not the active window, or Jump first is selected without a jump skill (State: not configured).'],
          [<strong className="text-white">Auto always says the route is not running</strong>, <>Automatic mounting only works with a route started on{' '}<button onClick={() => navigate('autopilot')} className="text-brand-300 hover:text-brand-200 underline underline-offset-2">AUTOPILOT &rsaquo; Play</button>.</>],
          [<strong className="text-white">Mounts slowly although a cloak is set</strong>, 'Look at the State line for cloak slot unknown - plain mount, fast cloak not found in the bag - plain mount, or cloak did not go up - mounting anyway. The cloak has to be in a normal (unlocked) bag slot, and the bag has to have been read.'],
        ]}
      />
    </div>
  );
}

export default PageFastMount;
